import React, { ComponentProps, FC, useMemo } from 'react';
import { InterestRate, withStyles } from 'vopay';
import { AccountType } from '@/components/Accounts/AccountSummary/AccountSummaryPage';
import AnnualRatesPopover from '../AnnualRatesPopover/AnnualRatesPopover';
import { Tooltip } from 'radix-ui';
import styles from './AnnualRatesTooltip.module.scss';

const cn = withStyles(styles);

type AnnualRatesTooltipProps = {
  accountType: AccountType;
  balance: number;
  interestRates: InterestRate[];
  trigger: React.JSX.Element;
  align?: ComponentProps<typeof Tooltip.Content>['align'];
};

const AnnualRatesTooltip: FC<AnnualRatesTooltipProps> = ({ accountType, balance, interestRates, trigger, align }) => {
  const annualRates = interestRates.filter((rate) =>
    accountType === 'standard' ? rate.Term === 'short-term' : rate.Term === 'long-term',
  );

  //Filter out when both RangeStart and RangeEnd are invalid
  const filteredRates = useMemo(() => {
    return annualRates.filter(({ RangeStart, RangeEnd }) => {
      return RangeStart > 0 || RangeEnd > 0;
    });
  }, [annualRates]);

  // using radix tooltip because our current one doesn't have a position="bottomRight" and it's easier just to make a brand new tooltip (until we re-do the tooltips, anyway)
  return (
    <Tooltip.Provider delayDuration={100}>
      <Tooltip.Root>
        <Tooltip.Trigger>{trigger}</Tooltip.Trigger>

        <Tooltip.Portal>
          <Tooltip.Content className={cn('contentContainer')} sideOffset={5} align={align}>
            <div className={cn('content')}>
              <AnnualRatesPopover accountType={accountType} balance={balance} interestRates={filteredRates} />
            </div>

            <Tooltip.Arrow asChild>
              <div className={cn('arrow')} />
            </Tooltip.Arrow>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default AnnualRatesTooltip;
